import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal } from "antd";
import dayjs from "dayjs";
import { PiPrinterFill } from "react-icons/pi";

import logo from "../../../assets/images/logo_nsf.png";
import ProformaService from "../../../service/ERP/Proforma.service";
import { formatMoney } from "../../../utils/util";
import "../jo-invoice/jo-invoice.css";
import "../invoice/invoice-layout.css";

const piService = ProformaService();
const PAGE_STYLE_ID = "proforma-print-page-style";
/* จำนวนแถวสินค้าสูงสุดที่ 1 หน้า A4 รองรับได้ (ใช้เกณฑ์เดียวกับ Commercial Invoice) */
const ITEMS_PER_PAGE = 9;

const EXPORTER = {
  company: "NINE STAR FOOD CO., LTD.",
  address: "54/5 MOO 4, TAMBON KHAOWUA, AMPHOE THAMAI, CHANTHABURI 22120, THAILAND",
  tel: "TEL +66 (0)81 8373092, (0)85 3674408",
  declare:
    "Nine Star Food declares that all the information contained in this proforma invoice to be true and correct",
};

const num = (v) => Number(v || 0);

/* รูปแบบวันที่ : ใช้ของลูกค้าถ้าตั้งไว้ ไม่งั้นใช้ DD/MM/YYYY */
const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

/* ลูกค้าขอให้ระบุ "ประเทศ" ต่อท้ายชื่อเมือง/ท่าเรือด้วย
   ถ้าในข้อความมีชื่อประเทศอยู่แล้วจะไม่เติมซ้ำ */
const withCountry = (place, country) => {
  const p = String(place || "").trim().toUpperCase();
  const c = String(country || "").trim().toUpperCase();
  if (!p || !c) return p;
  return p.includes(c) ? p : `${p}, ${c}`;
};

/* เดาชื่อประเทศปลายทางจากท่อนสุดท้ายของที่อยู่ผู้รับสินค้า */
const guessCountry = (address) => {
  const parts = String(address || "")
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
};
const fdate = (d, fmt) => {
  if (!d) return "";
  const f = String(fmt || "").trim() || DEFAULT_DATE_FORMAT;
  const out = dayjs(d).format(f);
  /* ถ้ารูปแบบที่กรอกใช้ไม่ได้ ให้ถอยกลับไปใช้ค่าเริ่มต้น */
  return out && out !== "Invalid Date" ? out : dayjs(d).format(DEFAULT_DATE_FORMAT);
};

/* ---- จำนวนเงินเป็นตัวอักษรภาษาอังกฤษ ---- */
const ONES = ["", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE",
  "TEN", "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN",
  "EIGHTEEN", "NINETEEN"];
const TENS = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];

function below1000(n) {
  let out = "";
  if (n >= 100) {
    out += ONES[Math.floor(n / 100)] + " HUNDRED";
    n %= 100;
    if (n) out += " ";
  }
  if (n >= 20) {
    out += TENS[Math.floor(n / 10)];
    n %= 10;
    if (n) out += " " + ONES[n];
  } else if (n > 0) {
    out += ONES[n];
  }
  return out;
}

function amountToWords(amount) {
  let n = Math.floor(num(amount));
  if (n === 0) return "ZERO";
  const scales = [[1000000000, "BILLION"], [1000000, "MILLION"], [1000, "THOUSAND"]];
  const parts = [];
  for (const [value, name] of scales) {
    if (n >= value) {
      parts.push(below1000(Math.floor(n / value)) + " " + name);
      n %= value;
    }
  }
  if (n > 0) parts.push(below1000(n));
  return parts.join(" ").trim();
}

function buildPageRows(rows) {
  const pages = [];
  for (let index = 0; index < rows.length; index += ITEMS_PER_PAGE) {
    const chunk = rows.slice(index, index + ITEMS_PER_PAGE);
    while (chunk.length < ITEMS_PER_PAGE) {
      chunk.push({ key: `empty-${index + chunk.length}`, isEmpty: true });
    }
    pages.push(chunk);
  }
  return pages.length > 0
    ? pages
    : [Array.from({ length: ITEMS_PER_PAGE }, (_, index) => ({ key: `empty-${index}`, isEmpty: true }))];
}

function InfoRow({ label, value }) {
  return (
    <div className="iv-inforow">
      <span className="iv-infolabel">{label}</span>
      <span className="iv-infovalue">{value}</span>
    </div>
  );
}

function InvoicePage({
  consignee, notifyParty, meta, bank, pageRows, total, weights,
  isLastPage, pageIndex, totalPages,
}) {
  return (
    <section className={`jo-invoice-page${isLastPage ? " is-last-page" : " has-page-break"}`}>
      {/* ── หัวเอกสาร ── */}
      <header className="iv-top">
        <img src={logo} alt="NSF logo" className="iv-logo" />
        <div className="iv-doctitle">
          PROFORMA INVOICE
          {totalPages > 1 && <span className="iv-pageno"> ({pageIndex + 1}/{totalPages})</span>}
        </div>
      </header>
      <div className="iv-rule" />

      {/* ── ผู้ส่งออก + เลขที่เอกสาร ── */}
      <section className="iv-parties">
        <div className="iv-party">
          <div className="iv-party-label">EXPORTER</div>
          <div className="iv-party-body">
            <div className="iv-party-name">{EXPORTER.company}</div>
            <div className="iv-party-line">{EXPORTER.address}</div>
            <div className="iv-party-line">{EXPORTER.tel}</div>
          </div>
        </div>
        <div className="iv-info">
          <InfoRow label="PROFORMA INV. NO." value={meta.invoiceNo} />
          <InfoRow label="DATE" value={fdate(meta.date, meta.dateFormat)} />
          <InfoRow label="CUSTOMER ID" value={meta.customerId} />
          {meta.invLinkNo ? <InfoRow label="INVOICE NO." value={meta.invLinkNo} /> : null}
        </div>
      </section>

      {/* ── ผู้รับสินค้า + Notify party ── */}
      <section className="iv-parties iv-parties-2">
        <div className="iv-party">
          <div className="iv-party-label">CONSIGNEE</div>
          <div className="iv-party-body">
            <div className="iv-party-name">{consignee.name}</div>
            <div className="iv-party-line">{consignee.address}</div>
            {consignee.contact ? <div className="iv-party-line">Contact: {consignee.contact}</div> : null}
            {consignee.tel ? <div className="iv-party-line">Tel: {consignee.tel}</div> : null}
            {consignee.email ? <div className="iv-party-line">E-mail: {consignee.email}</div> : null}
          </div>
        </div>
        <div className="iv-party">
          <div className="iv-party-label">NOTIFY PARTY</div>
          <div className="iv-party-body iv-pre">{notifyParty}</div>
        </div>
      </section>

      {/* ── เงื่อนไขการค้า ── */}
      <table className="iv-tbl iv-terms">
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "26%" }} />
          <col style={{ width: "23%" }} />
          <col style={{ width: "15%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>CUSTOMER P/O REF</th>
            <th>COUNTRY OF ORIGIN</th>
            <th>PAYMENT TERM</th>
            <th>INCOTERMS</th>
            <th>CURRENCY</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="is-center">{meta.customerPo}</td>
            <td className="is-center">{meta.countryOfOrigin}</td>
            <td className="is-center">{meta.paymentTerm}</td>
            <td className="is-center">{meta.priceTerm}</td>
            <td className="is-center">{meta.currency}</td>
          </tr>
        </tbody>
      </table>

      {/* ── ข้อมูลการขนส่ง ── */}
      <table className="iv-tbl iv-ship">
        <colgroup>
          <col style={{ width: "22%" }} />
          <col style={{ width: "22%" }} />
          <col style={{ width: "33%" }} />
          <col style={{ width: "23%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>PLACE OF RECEIPT</th>
            <th>FINAL DESTINATION</th>
            <th>CARRIER</th>
            <th>ETD / ETA</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="is-center">{meta.placeOfReceipt}</td>
            <td className="is-center">{meta.finalDestination}</td>
            <td className="is-center iv-pre-line">{meta.carrier}</td>
            <td className="is-center">
              {fdate(meta.etd, meta.dateFormat)} / {fdate(meta.eta, meta.dateFormat)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── ตารางสินค้า ── */}
      <table className="iv-tbl iv-items">
        <colgroup>
          <col style={{ width: "15%" }} />
          <col style={{ width: "38%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>MARK</th>
            <th>DESCRIPTION OF GOODS</th>
            <th>PACKING SIZE</th>
            <th>QUANTITY CASE</th>
            <th>UNIT PRICE ({meta.currency})</th>
            <th>AMOUNT TOTAL ({meta.currency})</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, i) => (
            row.isEmpty ? (
              <tr key={row.key} className="iv-empty">
                <td>&nbsp;</td><td /><td /><td /><td /><td className="iv-amount" />
              </tr>
            ) : (
              <tr key={row.key}>
                <td className="is-center iv-mark iv-pre-line">
                  {pageIndex === 0 && i === 0 ? meta.mark : ""}
                </td>
                <td className="is-left iv-desc">
                  <div>{row.description}</div>
                  {meta.mark ? <div className="iv-brand">BRAND : {meta.mark}</div> : null}
                </td>
                <td className="is-center">{row.packingSize}</td>
                <td className="is-center">{formatMoney(row.cases, 0)}</td>
                <td className="is-right">{formatMoney(row.unitPrice, 2)}</td>
                <td className="is-right iv-amount">{formatMoney(row.amount, 2)}</td>
              </tr>
            )
          ))}
          {isLastPage && (
            <tr className="iv-total">
              <td colSpan={2} className="iv-total-blank" />
              <td className="is-right">{meta.totalLabel}</td>
              <td className="is-center">{formatMoney(weights.cartons, 0)}</td>
              <td />
              <td className="is-right iv-amount">{formatMoney(total, 2)}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ── ท้ายเอกสาร ── */}
      {isLastPage && (
        <>
          <div className="iv-words">
            {meta.currency} {amountToWords(total)} ONLY
          </div>

          <table className="iv-tbl iv-weight">
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "56%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>NET WEIGHT (KGS)</th>
                <th>GROSS WEIGHT (KGS)</th>
                <th className="iv-blank-head" />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="is-center">{weights.net > 0 ? formatMoney(weights.net, 2) : ""}</td>
                <td className="is-center">{weights.gross > 0 ? formatMoney(weights.gross, 2) : ""}</td>
                <td className="iv-blank-cell" />
              </tr>
            </tbody>
          </table>

          <table className="iv-tbl iv-bank">
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "78%" }} />
            </colgroup>
            <thead>
              <tr><th colSpan={2}>BANK ACCOUNT DETAILS</th></tr>
            </thead>
            <tbody>
              <tr><td className="iv-bank-key">Account Name:</td><td>{bank.accName}</td></tr>
              <tr>
                <td className="iv-bank-key">Bank Name and Address:</td>
                <td>{[bank.bankName, bank.branch, bank.address].filter(Boolean).join(", ")}</td>
              </tr>
              <tr><td className="iv-bank-key">Account No.:</td><td>{bank.accNo}</td></tr>
              <tr><td className="iv-bank-key">SWIFT:</td><td>{bank.swiftCode}</td></tr>
            </tbody>
          </table>

          {meta.remark && (
            <div className="iv-remark">
              <span className="iv-remark-label">REMARK :</span>
              <span className="iv-remark-text">{meta.remark}</span>
            </div>
          )}

          <div className="iv-thanks">&quot;CERTIFIED TRUE AND CORRECT&quot;</div>
          <div className="iv-declare">{EXPORTER.declare}</div>

          <div className="iv-signs">
            <div className="iv-sign">
              <span className="iv-sign-label">Date:</span>
              <span className="iv-sign-line">{fdate(meta.date, meta.dateFormat)}</span>
            </div>
            <div className="iv-sign">
              <span className="iv-sign-label">Signature and Stamp:</span>
              <span className="iv-sign-line" />
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function ProformaPrintPreview() {
  const { code } = useParams();

  const [header, setHeader] = useState({});
  const [details, setDetails] = useState([]);

  const handlePrint = () => window.print();

  useEffect(() => {
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const apply = (v) => {
      htmlStyle.setProperty("overflow", v, "important");
      htmlStyle.setProperty("overflow-y", v, "important");
      bodyStyle.setProperty("overflow", v, "important");
      bodyStyle.setProperty("overflow-y", v, "important");
    };
    apply("auto");

    /* @page scope ตาม element ไม่ได้ และไฟล์ print อื่นถูกรวมมาในบันเดิลเดียวกัน
       จึงฉีดกฎขนาดกระดาษของใบนี้ตอนสั่งพิมพ์ ให้เป็นตัวท้ายสุดเสมอ แล้วถอดออกหลังพิมพ์ */
    const injectPageRule = () => {
      let el = document.getElementById(PAGE_STYLE_ID);
      if (!el) {
        el = document.createElement("style");
        el.id = PAGE_STYLE_ID;
        el.media = "print";
        el.innerHTML = "@page { size: A4 portrait; margin: 0; }";
        document.head.appendChild(el);
      }
    };
    const removePageRule = () => {
      const el = document.getElementById(PAGE_STYLE_ID);
      if (el) el.remove();
    };

    const onBefore = () => {
      injectPageRule();
      apply("visible");
    };
    const onAfter = () => {
      removePageRule();
      apply("auto");
    };
    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);
    return () => {
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
      removePageRule();
    };
  }, []);

  useEffect(() => {
    piService
      .get(code)
      .then((res) => {
        const { header: h, detail: d } = res.data?.data || {};
        setHeader(h || {});
        setDetails(d || []);
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "Request failed. Please try again.",
          onOk: () => window.close(),
        });
      });
  }, [code]);


  /* ผู้รับสินค้า — สำเนาที่เลือกไว้ตอนสร้าง invoice */
  const consignee = useMemo(() => ({
    name: header?.consignee_name || "",
    address: header?.consignee_address || "",
    contact: header?.consignee_contact || "",
    email: header?.consignee_email || "",
    tel: header?.consignee_tel || "",
  }), [header]);

  const bankRow = Array.isArray(header?.banks) && header.banks.length > 0 ? header.banks[0] : null;
  const bank = {
    swiftCode: bankRow?.swift_code || header?.swift_code || "",
    accName: bankRow?.acc_name || header?.bank_beneficiary || EXPORTER.company,
    accNo: bankRow?.acc_no || header?.bank_account || "",
    branch: bankRow?.bank_branch || header?.bank_branch || "",
    bankName: bankRow?.bank_name || header?.bank_name || "",
    address: bankRow?.bank_address || header?.bank_address || "",
  };

  /* Notify party — เว้นว่างไว้ให้ใช้ข้อมูล consignee เดียวกัน */
  const notifyParty =
    (header?.notify_party || "").trim() ||
    [consignee.name, consignee.address].filter(Boolean).join("\n");

  const meta = useMemo(() => ({
    invoiceNo: header?.pi_code || "",
    date: header?.pi_date,
    mark: header?.brand || "",
    customerPo: header?.customer_po || "",
    customerId: header?.cuscode || "",
    invLinkNo: header?.inv_code || (header?.inv_reserved === 1 || header?.inv_reserved === "1"
      ? `NSF${String(header?.runno || "").padStart(4, "0")}` : ""),
    currency: header?.currency || "",
    priceTerm: header?.delivery_term || header?.fob_term || "",
    /* ป้ายแถวรวม : INCOTERM + สถานที่ เช่น TOTAL FOB LAEM CHABANG, THAILAND */
    totalLabel: `TOTAL ${[
      String(header?.delivery_term || "").trim(),
      withCountry(header?.place_of_receipt, header?.country_of_origin || "THAILAND"),
    ]
      .filter(Boolean)
      .join(" ")}`.trim().toUpperCase(),
    paymentTerm: header?.payment_term || "",
    portOfLoading: header?.port_of_loading || "",
    placeOfReceipt: withCountry(header?.place_of_receipt, header?.country_of_origin || "THAILAND"),
    finalDestination: withCountry(
      header?.final_destination,
      header?.destination_country || guessCountry(header?.consignee_address)
    ),
    carrier: header?.carrier || "",
    etd: header?.etd,
    eta: header?.eta,
    countryOfOrigin: header?.country_of_origin || "THAILAND",
    volumeCbm: header?.volume_cbm || "",
    remark: header?.remark || "",
    dateFormat: header?.cus_date_format || "",
  }), [header]);

  const rows = useMemo(() => (details || []).map((item, index) => {
    const cases = num(item?.carton);
    const unitPrice = num(item?.unit_price);
    const amount = num(item?.line_total || cases * unitPrice);
    return {
      key: `${item?.id || "row"}-${index}`,
      itemNo: item?.seq ?? index + 1,
      hsCode: item?.hs_code || "",
      description: item?.pg_name || "",
      packingSize: item?.packing_size || "",
      cases,
      unitPrice,
      amount,
      _netWeight: cases * num(item?.net_wt),
      _grossWeight: cases * num(item?.gross_wt),
    };
  }), [details]);

  const total = useMemo(
    () => (header?.total_amount != null
      ? num(header.total_amount)
      : rows.reduce((s, r) => s + r.amount, 0)),
    [rows, header?.total_amount]
  );

  const weights = useMemo(() => ({
    cartons: rows.reduce((s, r) => s + r.cases, 0),
    net: header?.net_weight != null
      ? num(header.net_weight)
      : rows.reduce((s, r) => s + r._netWeight, 0),
    gross: header?.gross_weight != null
      ? num(header.gross_weight)
      : rows.reduce((s, r) => s + r._grossWeight, 0),
  }), [rows, header?.net_weight, header?.gross_weight]);

  const rowPages = useMemo(() => buildPageRows(rows), [rows]);

  return (
    <div className="jo-invoice-screen inv-doc" id="jo-invoice">
      <div className="jo-invoice-toolbar">
        <Button className="bn-center bg-blue-400" onClick={handlePrint} icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}>
          PRINT
        </Button>
      </div>

      {/* ปล่อยความสูงตามจำนวนหน้าจริง (เดิม fix ไว้ n x 297mm ทำให้หน้าท้ายโดนตัด) */}
      <div className="jo-invoice-preview">
        {rowPages.map((pageRows, pageIndex) => (
          <InvoicePage
            key={`invoice-page-${pageIndex}`}
            consignee={consignee}
            notifyParty={notifyParty}
            meta={meta}
            bank={bank}
            pageRows={pageRows}
            total={total}
            weights={weights}
            isLastPage={pageIndex === rowPages.length - 1}
            pageIndex={pageIndex}
            totalPages={rowPages.length}
          />
        ))}
      </div>
    </div>
  );
}

export default ProformaPrintPreview;
